import { useState, useMemo, useCallback } from "react";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Search,
  Filter,
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ConfidenceBadge } from "./ConfidenceBadge";
import type { DiscoveredTransaction } from "@shared/discovery-types";
import { cn } from "@/lib/utils";

// Swiss account categories for categorization
const CATEGORY_OPTIONS = [
  // Revenue (3xxx)
  {
    code: "3200",
    name: "Sales Revenue",
    german: "Warenertrag",
    group: "Revenue",
  },
  {
    code: "3400",
    name: "Service Revenue",
    german: "Dienstleistungsertrag",
    group: "Revenue",
  },
  {
    code: "3600",
    name: "Other Revenue",
    german: "Übrige Erträge",
    group: "Revenue",
  },
  {
    code: "3800",
    name: "Financial Income",
    german: "Finanzertrag",
    group: "Revenue",
  },

  // Material Expenses (4xxx)
  {
    code: "4200",
    name: "Purchases of Goods",
    german: "Wareneinkauf",
    group: "Materials",
  },
  {
    code: "4400",
    name: "Purchases of Materials",
    german: "Materialeinkauf",
    group: "Materials",
  },

  // Personnel (5xxx)
  {
    code: "5200",
    name: "Salaries & Wages",
    german: "Löhne und Gehälter",
    group: "Personnel",
  },
  {
    code: "5700",
    name: "Social Security",
    german: "Sozialversicherung",
    group: "Personnel",
  },

  // Operating Expenses (6xxx)
  { code: "6100", name: "Rent", german: "Raumaufwand", group: "Operating" },
  {
    code: "6200",
    name: "Maintenance & Repairs",
    german: "Unterhalt",
    group: "Operating",
  },
  {
    code: "6300",
    name: "Vehicles",
    german: "Fahrzeugaufwand",
    group: "Operating",
  },
  {
    code: "6400",
    name: "Insurance",
    german: "Versicherung",
    group: "Operating",
  },
  { code: "6500", name: "Utilities", german: "Energie", group: "Operating" },
  {
    code: "6600",
    name: "Administration",
    german: "Verwaltung",
    group: "Operating",
  },
  {
    code: "6700",
    name: "IT & Communication",
    german: "Informatik",
    group: "Operating",
  },
  {
    code: "6800",
    name: "Marketing",
    german: "Werbeaufwand",
    group: "Operating",
  },
  {
    code: "6900",
    name: "Other Operating",
    german: "Übriger Betriebsaufwand",
    group: "Operating",
  },

  // Financial (6950+)
  {
    code: "6960",
    name: "Interest Expenses",
    german: "Zinsaufwand",
    group: "Financial",
  },
  {
    code: "6970",
    name: "Bank Charges",
    german: "Bankspesen",
    group: "Financial",
  },

  // Special
  {
    code: "1030",
    name: "Internal Transfer",
    german: "Interne Überweisung",
    group: "Internal",
  },
];

// Group categories for the select dropdown
const CATEGORY_GROUPS = [
  { key: "Revenue", label: "Revenue (3xxx)" },
  { key: "Materials", label: "Materials (4xxx)" },
  { key: "Personnel", label: "Personnel (5xxx)" },
  { key: "Operating", label: "Operating (6xxx)" },
  { key: "Financial", label: "Financial" },
  { key: "Internal", label: "Internal Transfers" },
];

// Pagination constants
const INITIAL_DISPLAY_COUNT = 50;
const LOAD_MORE_COUNT = 50;

interface TransactionCategorizationPanelProps {
  transactions: DiscoveredTransaction[];
  onCategorize: (
    transactionId: string,
    accountCode: string,
    accountName: string
  ) => void;
  onBulkCategorize?: (
    transactionIds: string[],
    accountCode: string,
    accountName: string
  ) => void;
  className?: string;
}

function formatCurrency(cents: number): string {
  const isNegative = cents < 0;
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
  }).format(Math.abs(cents) / 100);
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export function TransactionCategorizationPanel({
  transactions,
  onCategorize,
  onBulkCategorize,
  className,
}: TransactionCategorizationPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<
    "all" | "uncategorized" | "debit" | "credit"
  >("all");
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(
    new Set()
  );
  const [bulkCategory, setBulkCategory] = useState<string>("");
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);

  // Filter and search transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter(tx => {
      // Filter by type
      if (filterType === "uncategorized" && tx.accountCode) return false;
      if (filterType === "debit" && tx.type !== "debit") return false;
      if (filterType === "credit" && tx.type !== "credit") return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          tx.description.toLowerCase().includes(query) ||
          tx.counterparty?.toLowerCase().includes(query) ||
          tx.accountName?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [transactions, filterType, searchQuery]);

  // Reset display count when filters change
  useMemo(() => {
    setDisplayCount(INITIAL_DISPLAY_COUNT);
  }, [filterType, searchQuery]);

  // Displayed transactions (paginated)
  const displayedTransactions = useMemo(() => {
    return filteredTransactions.slice(0, displayCount);
  }, [filteredTransactions, displayCount]);

  // Pagination info
  const hasMore = displayCount < filteredTransactions.length;
  const remainingCount = filteredTransactions.length - displayCount;

  // Load more handler
  const handleLoadMore = useCallback(() => {
    setDisplayCount(prev =>
      Math.min(prev + LOAD_MORE_COUNT, filteredTransactions.length)
    );
  }, [filteredTransactions.length]);

  // Show all handler
  const handleShowAll = useCallback(() => {
    setDisplayCount(filteredTransactions.length);
  }, [filteredTransactions.length]);

  // Stats
  const stats = useMemo(() => {
    const total = transactions.length;
    const categorized = transactions.filter(tx => tx.accountCode).length;
    const uncategorized = total - categorized;
    const totalDebits = transactions
      .filter(tx => tx.type === "debit")
      .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
    const totalCredits = transactions
      .filter(tx => tx.type === "credit")
      .reduce((sum, tx) => sum + tx.amount, 0);
    return { total, categorized, uncategorized, totalDebits, totalCredits };
  }, [transactions]);

  // Handle category selection for single transaction
  const handleCategoryChange = useCallback(
    (transactionId: string, categoryCode: string) => {
      const category = CATEGORY_OPTIONS.find(c => c.code === categoryCode);
      if (category) {
        onCategorize(transactionId, category.code, category.name);
      }
    },
    [onCategorize]
  );

  // Handle bulk categorization
  const handleBulkCategorize = useCallback(() => {
    if (!bulkCategory || selectedTransactions.size === 0 || !onBulkCategorize)
      return;
    const category = CATEGORY_OPTIONS.find(c => c.code === bulkCategory);
    if (category) {
      onBulkCategorize(
        Array.from(selectedTransactions),
        category.code,
        category.name
      );
      setSelectedTransactions(new Set());
      setBulkCategory("");
    }
  }, [bulkCategory, selectedTransactions, onBulkCategorize]);

  // Toggle transaction selection
  const toggleSelection = useCallback((id: string) => {
    setSelectedTransactions(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Select all visible uncategorized
  const selectAllUncategorized = useCallback(() => {
    const uncategorizedIds = filteredTransactions
      .filter(tx => !tx.accountCode)
      .map(tx => tx.id);
    setSelectedTransactions(new Set(uncategorizedIds));
  }, [filteredTransactions]);

  if (transactions.length === 0) {
    return null;
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div
        className={cn(
          "rounded-lg border bg-card text-card-foreground shadow-sm",
          className
        )}
      >
        {/* Header */}
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div>
                <h3 className="font-semibold">Transactions ({stats.total})</h3>
                <p className="text-sm text-muted-foreground">
                  {stats.categorized} categorized, {stats.uncategorized} need
                  review
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Summary badges */}
              <div className="hidden sm:flex items-center gap-2">
                <Badge variant="outline" className="text-success">
                  <ArrowDownCircle className="h-3 w-3 mr-1" />
                  {formatCurrency(stats.totalCredits)}
                </Badge>
                <Badge variant="outline" className="text-destructive">
                  <ArrowUpCircle className="h-3 w-3 mr-1" />
                  {formatCurrency(stats.totalDebits)}
                </Badge>
              </div>
              {stats.uncategorized > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-warning/10 text-warning"
                >
                  {stats.uncategorized} uncategorized
                </Badge>
              )}
              {isExpanded ? (
                <ChevronUp className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t p-4 space-y-4">
            {/* Search and filters */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search transactions..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={filterType}
                onValueChange={v => setFilterType(v as typeof filterType)}
              >
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All transactions</SelectItem>
                  <SelectItem value="uncategorized">Uncategorized</SelectItem>
                  <SelectItem value="debit">Expenses only</SelectItem>
                  <SelectItem value="credit">Income only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bulk actions */}
            {onBulkCategorize && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllUncategorized}
                  disabled={stats.uncategorized === 0}
                >
                  Select uncategorized
                </Button>
                <span className="text-sm text-muted-foreground">
                  {selectedTransactions.size} selected
                </span>
                <div className="flex-1" />
                <Select value={bulkCategory} onValueChange={setBulkCategory}>
                  <SelectTrigger className="w-[200px]">
                    <Sparkles className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Bulk category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_GROUPS.map(group => (
                      <SelectGroup key={group.key}>
                        <SelectLabel>{group.label}</SelectLabel>
                        {CATEGORY_OPTIONS.filter(
                          c => c.group === group.key
                        ).map(category => (
                          <SelectItem key={category.code} value={category.code}>
                            {category.code} - {category.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={handleBulkCategorize}
                  disabled={selectedTransactions.size === 0 || !bulkCategory}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Apply
                </Button>
              </div>
            )}

            {/* Transaction count indicator */}
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Showing {displayedTransactions.length} of{" "}
                {filteredTransactions.length} transactions
              </span>
              {filteredTransactions.length !== transactions.length && (
                <span>
                  ({transactions.length} total, {filteredTransactions.length}{" "}
                  matching filters)
                </span>
              )}
            </div>

            {/* Transactions table */}
            <div className="rounded-md border max-h-[500px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    {onBulkCategorize && <TableHead className="w-[40px]" />}
                    <TableHead className="w-[100px]">Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[120px] text-right">
                      Amount
                    </TableHead>
                    <TableHead className="w-[200px]">Category</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={onBulkCategorize ? 5 : 4}
                        className="text-center text-muted-foreground py-8"
                      >
                        No transactions match your filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedTransactions.map(tx => (
                      <TableRow
                        key={tx.id}
                        className={cn(
                          !tx.accountCode && "bg-warning/5",
                          selectedTransactions.has(tx.id) && "bg-primary/10"
                        )}
                      >
                        {onBulkCategorize && (
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedTransactions.has(tx.id)}
                              onChange={() => toggleSelection(tx.id)}
                              className="h-4 w-4 rounded border-input"
                            />
                          </TableCell>
                        )}
                        <TableCell className="font-mono text-sm">
                          {formatDate(tx.date)}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium text-sm line-clamp-1">
                              {tx.counterparty || tx.description}
                            </div>
                            {tx.counterparty &&
                              tx.description !== tx.counterparty && (
                                <div className="text-xs text-muted-foreground line-clamp-1">
                                  {tx.description}
                                </div>
                              )}
                            <div className="flex items-center gap-2">
                              {tx.sourceFile && (
                                <Badge variant="outline" className="text-xs">
                                  {tx.sourceFile
                                    .split("/")
                                    .pop()
                                    ?.substring(0, 20)}
                                </Badge>
                              )}
                              {tx.categorizationSource === "auto" &&
                                tx.categorizationConfidence && (
                                  <ConfidenceBadge
                                    confidence={tx.categorizationConfidence}
                                    size="sm"
                                    showLabel={false}
                                  />
                                )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={cn(
                              "font-medium",
                              tx.type === "credit"
                                ? "text-success"
                                : "text-destructive"
                            )}
                          >
                            {tx.type === "credit" ? "+" : "-"}
                            {formatCurrency(tx.amount)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={tx.accountCode || ""}
                            onValueChange={v => handleCategoryChange(tx.id, v)}
                          >
                            <SelectTrigger
                              className={cn(
                                "w-full",
                                !tx.accountCode && "border-[var(--warning)]/50"
                              )}
                            >
                              <SelectValue placeholder="Select category..." />
                            </SelectTrigger>
                            <SelectContent>
                              {CATEGORY_GROUPS.map(group => (
                                <SelectGroup key={group.key}>
                                  <SelectLabel>{group.label}</SelectLabel>
                                  {CATEGORY_OPTIONS.filter(
                                    c => c.group === group.key
                                  ).map(category => (
                                    <SelectItem
                                      key={category.code}
                                      value={category.code}
                                    >
                                      <span className="font-mono text-xs mr-2">
                                        {category.code}
                                      </span>
                                      {category.name}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Load more / Show all buttons */}
            {hasMore && (
              <div className="flex items-center justify-center gap-3 pt-2">
                <Button variant="outline" size="sm" onClick={handleLoadMore}>
                  Load {Math.min(LOAD_MORE_COUNT, remainingCount)} more
                </Button>
                {remainingCount > LOAD_MORE_COUNT && (
                  <Button variant="ghost" size="sm" onClick={handleShowAll}>
                    Show all ({filteredTransactions.length})
                  </Button>
                )}
                <span className="text-sm text-muted-foreground">
                  {remainingCount} remaining
                </span>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
